import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n';
import { Home, DollarSign, FileText, Settings, LogOut, ChevronLeft, Menu, X, CalendarDays } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import DomioLogo from '../common/DomioLogo';
import NotificationBell from '../common/NotificationBell';
import LanguageSwitcher from '../common/LanguageSwitcher';

const NAV_ITEMS = [
  { to: '/app/tenant', icon: Home, labelKey: 'tenantNav.home', end: true },
  { to: '/app/tenant/financials', icon: DollarSign, labelKey: 'tenantNav.financials' },
  { to: '/app/tenant/documents', icon: FileText, labelKey: 'tenantNav.documents' },
  { to: '/app/tenant/inspections', icon: CalendarDays, labelKey: 'tenantNav.inspections' },
];

export const TenantSidebar = ({ collapsed, onToggle, onClose }) => {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 z-40 h-screen bg-gradient-to-b from-[#0D3E6B] to-[#071E35] transition-all duration-300 flex flex-col',
        collapsed ? 'w-16' : 'w-64',
      )}
      data-testid="tenant-sidebar"
    >
      <div className="flex h-16 items-center justify-between px-4 border-b border-white/10">
        {!collapsed ? (
          <DomioLogo size="sm" showText textClassName="text-white min-w-0" />
        ) : (
          <DomioLogo size="sm" className="!h-7 w-auto mx-auto" />
        )}
        <div className="flex items-center gap-1">
          {onClose && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-slate-300 hover:text-white hover:bg-white/10 lg:hidden"
            >
              <X className="h-5 w-5" />
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={onToggle}
            className="text-slate-300 hover:text-white hover:bg-white/10 hidden lg:flex"
          >
            {collapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto py-4 px-2">
        <ul className="space-y-1">
          {NAV_ITEMS.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                end={item.end}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-white/15 text-white shadow-sm'
                      : 'text-slate-300 hover:text-white hover:bg-white/10',
                  )
                }
                data-testid={`tenant-nav-${item.labelKey.split('.').pop()}`}
              >
                <item.icon className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
                {!collapsed && <span>{t(item.labelKey)}</span>}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>

      <div className="border-t border-white/10 p-4">
        <div
          className={cn(
            'mb-3 px-1 flex items-center gap-2',
            collapsed ? 'flex-col items-center' : 'justify-end',
          )}
        >
          <LanguageSwitcher variant="dropdown" dark compact={collapsed} />
          <NotificationBell />
        </div>

        <NavLink
          to="/app/settings"
          className={({ isActive }) =>
            cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium transition-colors mb-2',
              isActive
                ? 'bg-white/15 text-white'
                : 'text-slate-300 hover:text-white hover:bg-white/10',
            )
          }
          data-testid="tenant-nav-settings"
        >
          <Settings className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>{t('tenantNav.profile')}</span>}
        </NavLink>

        {!collapsed && user && (
          <div className="px-3 py-2 mb-2">
            <p className="text-xs text-slate-400 uppercase tracking-wider font-manrope">{t('nav.loggedInAs')}</p>
            <p className="text-sm text-white font-medium truncate">{user.name}</p>
            <p className="text-xs text-slate-400">{t('roles.tenant')}</p>
          </div>
        )}

        <button
          type="button"
          onClick={handleLogout}
          className="flex items-center gap-3 px-3 py-2.5 rounded-sm text-sm font-medium text-slate-300 hover:text-white hover:bg-white/10 transition-colors w-full"
          data-testid="tenant-logout-btn"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" strokeWidth={1.5} />
          {!collapsed && <span>{t('auth.logout')}</span>}
        </button>
      </div>
    </aside>
  );
};

export default TenantSidebar;
